/**
 * The note synth (`sound-and-music` design §3, contract §3): four instruments, played in
 * the host on the `sfx` bus so the voice cap and the Sound tab apply. Notes are a kid's own
 * music and are never library files.
 *
 * Pitch is a scale degree 1–8 on the C major pentatonic over two octaves, so any order a
 * kid picks sounds like a tune, or a note name `C4`–`C6` when a child names notes.
 */

export type Instrument = "bell" | "marimba" | "frog" | "whistle";

export const INSTRUMENTS: readonly Instrument[] = ["bell", "marimba", "frog", "whistle"];

export function isInstrument(x: unknown): x is Instrument {
  return typeof x === "string" && (INSTRUMENTS as readonly string[]).includes(x);
}

/** Degrees 1–8: C4 D4 E4 G4 A4 C5 D5 E5. */
const DEGREE_MIDI = [60, 62, 64, 67, 69, 72, 74, 76];
const NOTE_SEMITONE: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
const NAME_RE = /^([A-G])(#|b)?(\d)$/;

function midiToHz(midi: number): number {
  return 440 * Math.pow(2, (midi - 69) / 12);
}

/** The playable range for a note name: C4 … C6 (contract §3). */
const MIDI_LOW = 60;
const MIDI_HIGH = 84;

export interface ResolvedPitch {
  hz: number;
  /** The pitch that sounds: the one asked for, or its octave-folded name. */
  pitch: number | string;
  /** A name outside C4–C6, moved by octaves into it (contract §3): "E6→E5". */
  folded?: string;
}

/**
 * A degree or a note name, resolved; null for anything the contract does not allow. A name
 * outside C4–C6 is folded by octaves into the range rather than dropped: a child's "a high
 * one" written as E6 plays as E5, never as silence (the engine's probe, 2026-10-23).
 */
export function resolvePitch(pitch: unknown): ResolvedPitch | null {
  if (typeof pitch === "number") {
    if (!Number.isInteger(pitch) || pitch < 1 || pitch > 8) return null;
    return { hz: midiToHz(DEGREE_MIDI[pitch - 1]!), pitch };
  }
  if (typeof pitch === "string") {
    const m = NAME_RE.exec(pitch.trim());
    if (!m) return null;
    const semi = NOTE_SEMITONE[m[1]!]! + (m[2] === "#" ? 1 : m[2] === "b" ? -1 : 0);
    let octave = Number(m[3]);
    while (12 * (octave + 1) + semi > MIDI_HIGH) octave--;
    while (12 * (octave + 1) + semi < MIDI_LOW) octave++;
    const hz = midiToHz(12 * (octave + 1) + semi);
    if (octave === Number(m[3])) return { hz, pitch: pitch.trim() };
    const sounds = `${m[1]}${m[2] ?? ""}${octave}`;
    return { hz, pitch: sounds, folded: `${pitch.trim()}→${sounds}` };
  }
  return null;
}

/** Hz for a degree or a note name (folded into C4–C6), or null for anything else. */
export function pitchToHz(pitch: unknown): number | null {
  return resolvePitch(pitch)?.hz ?? null;
}

export interface NoteVoice {
  /** When the note has rung out, on the context clock. */
  endsAt: number;
  stop(): void;
}

/** How long each instrument rings, in seconds. */
const RING_S: Record<Instrument, number> = { bell: 1.4, marimba: 0.5, frog: 0.28, whistle: 0.4 };

/**
 * Play one note now. Every instrument is oscillators and gain, with a filter for the
 * frog, so it works on every `AudioContext` we ship on and needs no file.
 */
export function playNote(
  ctx: AudioContext,
  dest: AudioNode,
  instrument: Instrument,
  hz: number,
  gain: number,
): NoteVoice {
  const t0 = ctx.currentTime;
  const ring = RING_S[instrument];
  const out = ctx.createGain();
  out.gain.setValueAtTime(0, t0);
  out.connect(dest);
  const nodes: { stop(when?: number): void; disconnect(): void }[] = [];

  const osc = (type: OscillatorType, freq: number, level: number, decay: number): OscillatorNode => {
    const o = ctx.createOscillator();
    o.type = type;
    o.frequency.setValueAtTime(freq, t0);
    const g = ctx.createGain();
    g.gain.setValueAtTime(level, t0);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + decay);
    o.connect(g);
    g.connect(out);
    o.start(t0);
    o.stop(t0 + ring + 0.05);
    nodes.push(o);
    return o;
  };

  switch (instrument) {
    case "bell":
      out.gain.linearRampToValueAtTime(gain, t0 + 0.005);
      osc("sine", hz, 1, ring);
      osc("sine", hz * 2.4, 0.3, ring * 0.6);
      break;
    case "marimba":
      out.gain.linearRampToValueAtTime(gain, t0 + 0.004);
      osc("sine", hz, 1, ring);
      osc("sine", hz * 4, 0.15, ring * 0.35);
      break;
    case "frog": {
      // A soft bandpassed croak that drops in pitch, so the Lake's chorus sounds like
      // frogs and not sine beeps (design §3).
      out.gain.linearRampToValueAtTime(gain * 0.8, t0 + 0.02);
      const o = ctx.createOscillator();
      o.type = "sawtooth";
      o.frequency.setValueAtTime(hz * 0.5, t0);
      o.frequency.exponentialRampToValueAtTime(hz * 0.35, t0 + 0.18);
      const f = ctx.createBiquadFilter();
      f.type = "bandpass";
      f.frequency.setValueAtTime(hz * 1.2, t0);
      f.Q.setValueAtTime(6, t0);
      const g = ctx.createGain();
      g.gain.setValueAtTime(1, t0);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + ring);
      o.connect(f);
      f.connect(g);
      g.connect(out);
      o.start(t0);
      o.stop(t0 + ring + 0.05);
      nodes.push(o);
      break;
    }
    case "whistle": {
      out.gain.linearRampToValueAtTime(gain * 0.7, t0 + 0.03);
      const o = osc("sine", hz * 2, 1, ring);
      const lfo = ctx.createOscillator();
      lfo.frequency.setValueAtTime(6, t0);
      const depth = ctx.createGain();
      depth.gain.setValueAtTime(hz * 0.02, t0);
      lfo.connect(depth);
      depth.connect(o.frequency);
      lfo.start(t0);
      lfo.stop(t0 + ring + 0.05);
      nodes.push(lfo);
      break;
    }
  }

  return {
    endsAt: t0 + ring,
    stop() {
      try {
        out.gain.cancelScheduledValues(ctx.currentTime);
        out.gain.setValueAtTime(out.gain.value, ctx.currentTime);
        out.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.03);
        for (const n of nodes) n.stop(ctx.currentTime + 0.05);
      } catch {
        /* already stopped */
      }
    },
  };
}
